import type {
  Study,
  AucResult,
  BioavailabilityResult,
  ValidationWarning,
  WarningSeverity,
  PkParameters,
} from '@bioavail/shared-types';
import {
  WarningSeverity as WS,
  WarningCode as WC,
  CalculationMode as CM,
  AucInterval as AI,
  RouteOfAdministration as ROA,
} from '@bioavail/shared-types';
import { recommendSamplingExtension } from '../auc/lambdaZ';

/**
 * Evaluate all validation warnings (PSD §4.2.9 + anomaly checks, F-038/F-039).
 * Block warnings prevent computation; warn/info are advisory.
 */
export function evaluateWarnings(
  study: Study,
  aucResults: AucResult[],
  bioavailability: BioavailabilityResult,
  pkParameters: PkParameters[] = [],
): ValidationWarning[] {
  const warnings: ValidationWarning[] = [];

  // Negative / zero inputs (F-037) — checked across arms.
  for (const arm of study.arms) {
    if (arm.dose <= 0) {
      warnings.push({
        code: WC.NegativeInput,
        severity: WS.Block,
        message: `Arm "${arm.label}" dose must be greater than zero.`,
        remediation: 'Enter a positive dose value.',
        armId: arm.id,
      });
    }
    for (const p of arm.dataPoints) {
      if (p.time < 0) {
        warnings.push({
          code: WC.NegativeInput,
          severity: WS.Block,
          message: `Arm "${arm.label}" has a negative time point (${p.time}).`,
          remediation: 'Time values must be ≥ 0.',
          armId: arm.id,
        });
      }
      if (p.concentration < 0) {
        warnings.push({
          code: WC.NegativeInput,
          severity: WS.Block,
          message: `Arm "${arm.label}" has a negative concentration (${p.concentration}).`,
          remediation: 'Concentration values must be ≥ 0.',
          armId: arm.id,
        });
      }
    }
  }

  // F exceeds 100% (absolute mode only).
  if (bioavailability.mode === CM.Absolute && bioavailability.fDoseCorrected > 1) {
    warnings.push({
      code: WC.FExceedsOne,
      severity: WS.Warn,
      message: `F exceeds 100% (${bioavailability.fPercent.toFixed(1)}%) — check dose or unit assumptions.`,
      remediation: 'Verify the IV dose was entered correctly and units are compatible.',
    });
  }

  // Per-arm AUC-derived warnings.
  for (const auc of aucResults) {
    // High extrapolation.
    if (study.aucInterval === AI.ZeroToInf && auc.extrapolationPercent > 20) {
      // cLast is not propagated to AucResult; derive as λ_z · AUC_extrap
      // (since AUC_extrap = cLast / λ_z).
      const cLast = auc.lambdaZ > 0 ? auc.lambdaZ * auc.aucExtrapolated : 0;
      const rec = recommendSamplingExtension({
        lambdaZ: auc.lambdaZ,
        cLast,
        aucZeroToT: auc.aucZeroToT,
      });
      const extensionHint =
        rec.additionalHours > 0
          ? ` Extend sampling by ~${rec.additionalHours.toFixed(1)} h (≈ ${rec.additionalHalfLives.toFixed(1)} more t½) to bring extrap% under 20%.`
          : '';
      warnings.push({
        code: WC.HighExtrapolation,
        severity: WS.Warn,
        message: `AUC extrapolation is ${auc.extrapolationPercent.toFixed(1)}% (> 20%) for an arm — AUC(0-∞) may be unreliable.`,
        remediation: 'Extend sampling or use AUC(0-t); review terminal-phase selection.' + extensionHint,
        armId: auc.armId,
      });
    }
    // Low terminal R².
    if (study.aucInterval === AI.ZeroToInf && auc.lambdaZR2 > 0 && auc.lambdaZR2 < 0.85) {
      warnings.push({
        code: WC.LowTerminalR2,
        severity: WS.Warn,
        message: `Terminal-phase R² is ${auc.lambdaZR2.toFixed(3)} (< 0.85) for an arm — λ_z fit is poor.`,
        remediation: 'Manually override terminal point selection to improve the fit.',
        armId: auc.armId,
      });
    }
    // Negative predicted concentration (extrapolation goes negative).
    if (study.aucInterval === AI.ZeroToInf && auc.lambdaZ > 0 && auc.aucExtrapolated < 0) {
      warnings.push({
        code: WC.NegativePredictedConc,
        severity: WS.Warn,
        message: 'λ_z extrapolation predicts a negative concentration — terminal phase invalid.',
        remediation: 'Re-select terminal points; the curve must be declining.',
        armId: auc.armId,
      });
    }
    // Sampling adequacy (F-039).
    if (
      study.aucInterval === AI.ZeroToInf &&
      auc.aucZeroToInf > 0 &&
      auc.aucZeroToT / auc.aucZeroToInf < 0.8
    ) {
      warnings.push({
        code: WC.SamplingInadequate,
        severity: WS.Warn,
        message: `AUC(0-t) covers only ${((auc.aucZeroToT / auc.aucZeroToInf) * 100).toFixed(1)}% of AUC(0-∞) — sampling is inadequate.`,
        remediation: 'Extend the sampling window to capture more of the elimination phase.',
        armId: auc.armId,
      });
    }
    // Washout sufficiency (F-015).
    if (study.aucInterval === AI.ZeroToInf && auc.aucZeroToInf > 0) {
      // Heuristic: if extrapolation is very high (> 40%), washout likely insufficient.
      if (auc.extrapolationPercent > 40) {
        warnings.push({
          code: WC.WashoutInsufficient,
          severity: WS.Warn,
          message: 'Washout may be insufficient for this sampling window.',
          remediation: 'Ensure sampling covers at least 3 terminal half-lives.',
          armId: auc.armId,
        });
      }
    }
  }

  // Phase 2 info reminders (always harmless here; fire on unequal doses / differing analytes).
  const doses = study.arms.map((a) => a.dose);
  const dosesDiffer = doses.some((d) => Math.abs(d - doses[0]!) > 1e-9);
  if (dosesDiffer) {
    warnings.push({
      code: WC.DoseMatchReminder,
      severity: WS.Info,
      message: 'Arm doses differ — dose-normalization is being applied.',
      remediation: 'Confirm the dose ratio is intended for cross-arm comparison.',
    });
  }
  const analytes = new Set(study.arms.map((a) => a.analyteType));
  if (analytes.size > 1) {
    warnings.push({
      code: WC.AnalyteMismatch,
      severity: WS.Warn,
      message: 'Arms use different analyte types (parent vs metabolite).',
      remediation: 'Ensure both arms measure the same analyte for a valid comparison.',
    });
  }

  // Flip-flop kinetics (F-066): oral arm where absorption (MAT) is slower than
  // elimination (t½) — terminal slope reflects absorption, not elimination.
  const oralArm = study.arms.find((a) => a.route !== ROA.IV);
  if (oralArm) {
    const oralPk = pkParameters.find((p) => p.armId === oralArm.id);
    const halfLife = oralPk?.halfLife ?? null;
    const mat = oralPk?.mat ?? undefined;
    if (mat !== undefined && halfLife !== null && mat > halfLife) {
      warnings.push({
        code: WC.FlipFlopKinetics,
        severity: WS.Warn,
        message: `Absorption (MAT=${mat.toFixed(1)} h) is slower than elimination (t½=${halfLife.toFixed(1)} h) — flip-flop kinetics; terminal slope reflects absorption, not elimination.`,
        remediation:
          'The terminal phase is absorption-rate-limited. Cite t½ from an IV or non-flip-flop reference, or extend sampling past the absorption tail.',
        armId: oralArm.id,
      });
    }
  }

  // PK inconsistency across arms (F-071): same drug should share elimination.
  if (pkParameters.length >= 2) {
    const terminalForArm = (pk: PkParameters): number | null => {
      if (pk.halfLife !== null) return pk.halfLife;
      // Fallback to λ_z from the matching AUC result when halfLife is null.
      const auc = aucResults.find((a) => a.armId === pk.armId);
      return auc && auc.lambdaZ > 0 ? auc.lambdaZ : null;
    };
    const present = pkParameters
      .map(terminalForArm)
      .filter((t): t is number => t !== null);
    if (present.length >= 2) {
      const t1 = present[0]!;
      const t2 = present[1]!;
      const diff = Math.abs(t1 - t2) / Math.max(t1, t2);
      if (diff > 0.25) {
        warnings.push({
          code: WC.PkInconsistentAcrossArms,
          severity: WS.Warn,
          message: `Terminal kinetics differ across arms (test t½=${t1}, ref t½=${t2}, ${(diff * 100).toFixed(0)}% difference) — same drug should share elimination.`,
          remediation:
            'Review terminal-point selection on both arms; verify both measure the same analyte.',
        });
      }
    }
  }

  return warnings;
}

export function severityRank(s: WarningSeverity): number {
  switch (s) {
    case WS.Block:
      return 0;
    case WS.Warn:
      return 1;
    case WS.Info:
      return 2;
  }
}

export { WS, WC };
